import api from 'api';
import {
  getFromState,
  getAccessToken,
  logError,
  mergeAllAroundLeaderboards,
} from 'helpers';
import {
  ALL_AROUND_COMPETITION_LEVELS,
  getAllAroundDisciplines,
} from 'constants/allAround';

export const LEADERBOARD_ASYNC_START = 'LEADERBOARD_ASYNC_START';
export const LEADERBOARD_ASYNC_SUCCESS = 'LEADERBOARD_ASYNC_SUCCESS';
export const LEADERBOARD_ASYNC_FAIL = 'LEADERBOARD_ASYNC_FAIL';

function leaderboardAsyncStart() {
  return {
    type: LEADERBOARD_ASYNC_START,
  };
}

function leaderboardAsyncSuccess(data) {
  return {
    type: LEADERBOARD_ASYNC_SUCCESS,
    data,
  };
}

function leaderboardAsyncFail(error) {
  return {
    type: LEADERBOARD_ASYNC_FAIL,
    error,
  };
}

function getLeaderboardPath(season, discipline, segment, type) {
  const basePath = 'contestants/leaders?';
  const seasonSuffix = season ? `season=${season}&` : '';
  const disciplineSuffix = discipline ? `discipline=${discipline}&` : '';
  const segmentSuffix = segment ? `segment=${segment}&` : '';
  const typeSuffix = type ? `type=${type}&` : '';

  return (
    basePath + seasonSuffix + disciplineSuffix + segmentSuffix + typeSuffix
  );
}

// Leaderboard requests overlap. All Around fans out across every discipline in
// the segment, so it can still be in flight when a single-discipline request
// started later has already come back. Whichever answers last would otherwise
// win, and the table would show one standing while the dropdown names another.
//
// Every request takes a number and only the newest one is allowed to write to
// the store. Both loaders share the counter because they write to the same
// place - a stale All Around response has to lose to a newer plain one, and the
// other way round.
let latestLeaderboardRequest = 0;

const isStaleLeaderboardRequest = (requestId) =>
  requestId !== latestLeaderboardRequest;

export function getLeaderboard(values) {
  return async (dispatch, getState) => {
    const { season, discipline, segment, type } = values;
    const { authPayload } = getFromState(getState, 'auth');
    const accessToken = getAccessToken(authPayload);
    const path = getLeaderboardPath(season, discipline, segment, type);
    latestLeaderboardRequest += 1;
    const requestId = latestLeaderboardRequest;

    dispatch(leaderboardAsyncStart());
    try {
      const response = await api({
        path,
        method: 'GET',
        header: {
          Accept: 'application/json',
        },
        accessToken,
      });

      if (isStaleLeaderboardRequest(requestId)) return null;

      dispatch(leaderboardAsyncSuccess({ leaders: response }));
      return response;
    } catch (error) {
      logError(error);
      if (isStaleLeaderboardRequest(requestId)) return null;

      const err = 'There was a problem getting leader board, please try again.';
      dispatch(leaderboardAsyncFail(err));
      return null;
    }
  };
}

/**
 * Loads the All Around standing: every discipline in the segment fetched and
 * then summed per athlete.
 *
 * The leaders endpoint answers for one discipline at a time, so this is the one
 * place in the app that fans out. The requests run together rather than in
 * sequence - a segment can carry six disciplines and doing them one after
 * another is six round trips of waiting.
 *
 * Dispatches the same shape as getLeaderboard, so everything downstream - the
 * reducer, the table, the loading flag - is unchanged and does not need to know
 * this leaderboard was assembled rather than fetched.
 */
export function getAllAroundLeaderboard(values) {
  return async (dispatch, getState) => {
    const {
      season,
      segment,
      disciplines = [],
      competitionLevels = ALL_AROUND_COMPETITION_LEVELS,
    } = values;
    const { authPayload } = getFromState(getState, 'auth');
    const accessToken = getAccessToken(authPayload);
    latestLeaderboardRequest += 1;
    const requestId = latestLeaderboardRequest;

    dispatch(leaderboardAsyncStart());
    try {
      // Every counting discipline at every counting level. There is one all
      // around standing rather than one per level, so Pro and Challenger are
      // fetched together and summed per athlete - that is what makes a single
      // bonus answerable.
      //
      // Not every discipline counts towards the total, and the list is
      // deduplicated on the way through - see getAllAroundDisciplines.
      const requests = getAllAroundDisciplines(disciplines).flatMap(
        (discipline) =>
          competitionLevels.map((competitionLevel) =>
            api({
              path: getLeaderboardPath(
                season,
                discipline,
                segment,
                competitionLevel,
              ),
              method: 'GET',
              header: { Accept: 'application/json' },
              accessToken,
            }).catch((error) => {
              // One board failing should not lose the whole standing. Log it
              // and carry on with the rest, which is closer to right than
              // showing nothing at all.
              logError(error);
              return [];
            }),
          ),
      );

      const boards = await Promise.all(requests);

      if (isStaleLeaderboardRequest(requestId)) return null;

      const leaders = mergeAllAroundLeaderboards(boards);
      dispatch(leaderboardAsyncSuccess({ leaders }));
      return leaders;
    } catch (error) {
      logError(error);
      if (isStaleLeaderboardRequest(requestId)) return null;

      const err = 'There was a problem getting leader board, please try again.';
      dispatch(leaderboardAsyncFail(err));
      return null;
    }
  };
}
